import { DomainError } from "../domain/entities.js";
import { parseHorizonKey } from "../horizon/horizon.js";
import { stampNew, stampUpdate } from "./context.js";
/**
 * Інваріанти повторюють CHECK у схемі навмисно. База — остання лінія;
 * домен має відмовити раніше й зрозумілішим повідомленням, ніж SQLITE_CONSTRAINT.
 */
function validate(input) {
    if (input.kind === "note" && !input.title?.trim()) {
        throw new DomainError("нотатка без назви не знаходиться пошуком і губиться");
    }
    if (input.kind === "journal") {
        if (!input.entryDate)
            throw new DomainError("щоденниковий запис без дати не має де показатися");
        const h = parseHorizonKey(input.entryDate);
        if (h?.kind !== "day")
            throw new DomainError(`«${input.entryDate}» не є датою дня`);
    }
    if (input.mood != null && (input.mood < 1 || input.mood > 5)) {
        throw new DomainError("настрій буває від 1 до 5");
    }
}
export async function createEntry(ctx, input) {
    validate(input);
    const entry = {
        id: ctx.ids.next(),
        kind: input.kind,
        title: input.title?.trim() || null,
        body: input.body ?? null,
        entryDate: input.kind === "journal" ? input.entryDate : (input.entryDate ?? null),
        mood: input.mood ?? null,
        ...stampNew(ctx),
    };
    await ctx.entries.save(entry);
    await indexEntry(ctx, entry);
    await relinkEntry(ctx, entry);
    return entry;
}
/**
 * Тримати запис і його місце в пошуку узгодженими — обовʼязок одного місця.
 *
 * Видалений запис знімається з індексу, а не переіндексовується порожнім:
 * порожній рядок в індексі однаково знайшовся б за назвою.
 *
 * Експортована й звужена до `{ search }` навмисно: її кличе ще й аплаєр
 * синхронізації, у якого повного `Ctx` немає. Друга копія цієї логіки
 * розійшлася б із першою тихо — і саме на видаленні, тобто там, де
 * помилку видно найпізніше.
 */
export async function indexEntry(ctx, entry) {
    if (entry.deletedAt !== null) {
        await ctx.search.remove(entry.id);
        return;
    }
    await ctx.search.index(entry.id, entry.title ?? "", entry.body);
}
/**
 * Тримати звʼязки згадок узгодженими з тілом — той самий обовʼязок, що й
 * з пошуком, і той самий спосіб його виконати.
 *
 * Перебудова, а не доповнення: за [ADR-0032] звʼязок є **похідною** від
 * тіла, тож стерта згадка має зникати сама. Якби тут було «додати
 * звʼязок», прибирати його довелося б окремим рухом у кожному шляху
 * редагування — і хтось із них про це забув би.
 *
 * Звужена до `{ links }` і експортована з тієї ж причини, що `indexEntry`:
 * її кличе ще й аплаєр синхронізації, у якого повного `Ctx` немає.
 */
export async function relinkEntry(ctx, entry) {
    if (entry.deletedAt !== null) {
        await ctx.links.remove(entry.id);
        return;
    }
    await ctx.links.relink(entry.id, entry.body);
}
export async function updateEntry(ctx, id, patch) {
    const current = await ctx.entries.byId(id);
    if (!current || current.deletedAt)
        throw new DomainError(`запису ${id} немає`);
    const merged = { ...current, ...patch };
    validate({
        kind: merged.kind,
        title: merged.title,
        entryDate: merged.entryDate,
        mood: merged.mood,
    });
    const next = stampUpdate(ctx, merged);
    await ctx.entries.save(next);
    await indexEntry(ctx, next);
    await relinkEntry(ctx, next);
    return next;
}
/**
 * Змінити рід і дату запису — чіп роду й чіп дати в шторці B1 (картка
 * SheetEntry). Це головний шлях розбору інбокса: впіймане на ходу
 * отримує день і стає щоденниковим записом, не копіюючись.
 *
 * Дата йде разом із родом, а не окремою дією: `journal` без дати не має
 * де показатися, і проміжного стану «уже запис, ще без дня» бути не може.
 * Для нотатки й інбокса дата знімається — «без дати» там значення, а не
 * порожнє поле.
 */
export async function moveEntry(ctx, id, to) {
    const current = await ctx.entries.byId(id);
    if (!current || current.deletedAt)
        throw new DomainError(`запису ${id} немає`);
    const entryDate = to.kind === "journal" ? (to.entryDate ?? current.entryDate) : null;
    const merged = { ...current, kind: to.kind, entryDate };
    validate({ kind: merged.kind, title: merged.title, entryDate: merged.entryDate, mood: merged.mood });
    const next = stampUpdate(ctx, merged);
    await ctx.entries.save(next);
    await indexEntry(ctx, next);
    return next;
}
/**
 * Мʼяке видалення запису. Єдиний шлях: інакше кожне місце, що ставить
 * `deletedAt` руками, мусило б памʼятати ще й про індекс — і рано чи пізно
 * забуло б.
 */
export async function deleteEntry(ctx, id) {
    const current = await ctx.entries.byId(id);
    if (!current || current.deletedAt)
        throw new DomainError(`запису ${id} немає`);
    const next = stampUpdate(ctx, { ...current, deletedAt: ctx.clock.now() });
    await ctx.entries.save(next);
    await indexEntry(ctx, next);
    await relinkEntry(ctx, next);
    return next;
}
/** Записи дня. Видалені не повертаються: tombstone потрібен синхронізації, не інтерфейсу. */
export async function listDay(ctx, dayKey) {
    const h = parseHorizonKey(dayKey);
    if (h?.kind !== "day")
        throw new DomainError(`«${dayKey}» не є датою дня`);
    const rows = await ctx.entries.byDate(dayKey);
    return rows.filter((e) => !e.deletedAt).sort((a, b) => a.createdAt - b.createdAt);
}
/**
 * Записи днів проміжку, згруповані за днем. Один запит замість запиту на
 * день — див. `EntryRepo.inRange`. Дні без записів у мапі відсутні.
 */
export async function listDays(ctx, fromKey, toKey) {
    if (parseHorizonKey(fromKey)?.kind !== "day")
        throw new DomainError(`«${fromKey}» не є датою дня`);
    if (parseHorizonKey(toKey)?.kind !== "day")
        throw new DomainError(`«${toKey}» не є датою дня`);
    const byDay = new Map();
    for (const e of await ctx.entries.inRange(fromKey, toKey)) {
        if (e.deletedAt || e.entryDate === null)
            continue;
        const list = byDay.get(e.entryDate);
        if (list)
            list.push(e);
        else
            byDay.set(e.entryDate, [e]);
    }
    return byDay;
}
/** Найдешевший запис: інбокс без категоризації. Саме він стоїть за швидким захопленням. */
/**
 * Захоплене при замкненій базі стає звичайним записом інбокса.
 *
 * Відрізняється від `captureToInbox` двома речами, і обидві названі
 * в [ADR-0011]:
 *
 * - **ідентифікатор приходить ззовні** й виведений із самого блоба. Тому
 *   повторний розбір після обриву не створює другого запису, а натрапляє
 *   на наявний;
 * - **`createdAt` береться з навантаження**, тобто з годинника в мить, коли
 *   людина писала. Інакше все захоплене за ніч злиплося б в одну мить
 *   розблокування.
 *
 * HLC при цьому присвоюється **тут**, а не при захопленні: годинник у мить
 * захоплення довіри не заслуговує, і порядок злиття на ньому будувати не можна.
 */
export async function fileCapture(ctx, input) {
    const text = input.text.trim();
    if (!text)
        throw new DomainError("порожнє захоплення не зберігається");
    const existing = await ctx.entries.byId(input.id);
    if (existing)
        return existing;
    const entry = {
        ...stampNew(ctx),
        id: input.id,
        kind: "inbox",
        title: text.slice(0, 120),
        body: null,
        entryDate: null,
        mood: null,
        createdAt: input.at,
    };
    await ctx.entries.save(entry);
    await indexEntry(ctx, entry);
    await relinkEntry(ctx, entry);
    return entry;
}
export async function captureToInbox(ctx, text) {
    if (!text.trim())
        throw new DomainError("порожнє захоплення не зберігається");
    return createEntry(ctx, { kind: "inbox", title: text.trim().slice(0, 120) });
}
//# sourceMappingURL=entries.js.map